import { Check, ChevronsUpDown, LoaderCircle, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../ui/popover';
import type { OptionLoader, PickerOption } from '../types';

const DEBOUNCE_MS = 250;

/** Loads options for the typed query (debounced) while the popover is open. */
function useLoadedOptions(load: OptionLoader, open: boolean, query: string) {
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      load(query, controller.signal)
        .then((rows) => {
          if (!controller.signal.aborted) setOptions(rows);
        })
        .catch((e: unknown) => {
          if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, open, query]);
  return { options, loading, error };
}

interface PickerListProps {
  load: OptionLoader;
  open: boolean;
  selected: ReadonlySet<string>;
  onPick: (option: PickerOption) => void;
  searchPlaceholder?: string;
}

function PickerList({ load, open, selected, onPick, searchPlaceholder }: PickerListProps) {
  const [query, setQuery] = useState('');
  const { options, loading, error } = useLoadedOptions(load, open, query);
  return (
    <Command shouldFilter={false}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={searchPlaceholder ?? 'Search…'}
      />
      <CommandList>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-fg-muted text-sm">
            <LoaderCircle className="size-4 animate-spin" aria-hidden="true" /> Searching…
          </div>
        ) : error ? (
          <div role="alert" className="px-3 py-6 text-center text-danger text-sm">
            {error}
          </div>
        ) : (
          <>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem key={option.id} value={option.id} onSelect={() => onPick(option)}>
                  <Check
                    className={cn('size-4', selected.has(option.id) ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{option.label}</span>
                    {option.description ? (
                      <span className="truncate text-fg-muted text-xs">{option.description}</span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}

export interface PickerShellProps {
  id?: string;
  open: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
  className?: string;
  /** Chips or label rendered before the combobox button. */
  children?: ReactNode;
  /** Text shown inside the combobox button (placeholder or current label). */
  buttonLabel: ReactNode;
  onClear?: () => void;
}

/**
 * Input-looking container: chips + the real `<button role="combobox">` (popover trigger) + an
 * optional clear `<button>`. Buttons are siblings, never nested, so every control is a native
 * element. The popover anchors to the whole container (`PopoverAnchor`).
 */
export function PickerShell({
  id,
  open,
  disabled,
  invalid,
  required,
  describedBy,
  className,
  children,
  buttonLabel,
  onClear,
}: PickerShellProps) {
  return (
    <PopoverAnchor asChild>
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-border-strong bg-surface-raised px-2 py-1 text-sm shadow-xs',
          'has-[button[role=combobox]:focus-visible]:border-ring has-[button[role=combobox]:focus-visible]:ring-2 has-[button[role=combobox]:focus-visible]:ring-ring/30',
          disabled && 'cursor-not-allowed opacity-50',
          invalid && 'border-danger',
          className,
        )}
      >
        {children}
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            disabled={disabled}
            className="flex min-w-24 flex-1 items-center justify-between gap-2 rounded-sm px-1 text-left outline-none disabled:cursor-not-allowed"
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        {onClear && !disabled ? (
          <button
            type="button"
            aria-label="Clear"
            className="rounded-sm p-0.5 text-fg-muted hover:text-fg-muted"
            onClick={onClear}
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
    </PopoverAnchor>
  );
}

/** Removable chip used by the multi pickers. */
export function PickerChip({
  label,
  onRemove,
  color,
}: {
  label: string;
  onRemove?: () => void;
  color?: string | null;
}) {
  return (
    <Badge variant="secondary" dot={color ?? null}>
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          className="ml-0.5 rounded-sm hover:bg-border-strong"
          onClick={onRemove}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </Badge>
  );
}

const popoverClass = 'w-[var(--radix-popover-trigger-width)] min-w-64 p-0';

export interface AsyncPickerProps {
  id?: string;
  value: string | null | undefined;
  onChange: (id: string | undefined) => void;
  load: OptionLoader;
  /** Label for the current value when it is not among the loaded options (from a `display` map). */
  displayLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
  className?: string;
}

/** Single-value async combobox (user, ticket lookup). */
export function AsyncPicker(props: AsyncPickerProps) {
  const [open, setOpen] = useState(false);
  const labels = useRef(new Map<string, string>());
  const current = props.value ?? undefined;
  const label = current ? (labels.current.get(current) ?? props.displayLabel ?? current) : null;

  return (
    <Popover open={open} onOpenChange={props.disabled ? undefined : setOpen}>
      <PickerShell
        id={props.id}
        open={open}
        disabled={props.disabled}
        invalid={props.invalid}
        required={props.required}
        describedBy={props.describedBy}
        className={props.className}
        buttonLabel={
          label ?? <span className="text-fg-muted">{props.placeholder ?? 'Select…'}</span>
        }
        onClear={current && !props.required ? () => props.onChange(undefined) : undefined}
      />
      <PopoverContent className={popoverClass}>
        <PickerList
          load={props.load}
          open={open}
          selected={new Set(current ? [current] : [])}
          searchPlaceholder={props.searchPlaceholder}
          onPick={(option) => {
            labels.current.set(option.id, option.label);
            props.onChange(option.id === current ? undefined : option.id);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface AsyncMultiPickerProps extends Omit<AsyncPickerProps, 'value' | 'onChange'> {
  value: readonly string[] | null | undefined;
  onChange: (ids: string[] | undefined) => void;
  max?: number;
  /** Labels for ids already selected when the form loaded. */
  initialLabels?: Record<string, string>;
}

/** Multi-value async combobox (multi_user). */
export function AsyncMultiPicker(props: AsyncMultiPickerProps) {
  const [open, setOpen] = useState(false);
  const labels = useRef(new Map<string, string>(Object.entries(props.initialLabels ?? {})));
  const selected = props.value ?? [];
  const selectedSet = new Set(selected);

  const toggle = (option: PickerOption) => {
    labels.current.set(option.id, option.label);
    if (selectedSet.has(option.id)) {
      const next = selected.filter((v) => v !== option.id);
      props.onChange(next.length === 0 ? undefined : next);
    } else {
      if (props.max !== undefined && selected.length >= props.max) return;
      props.onChange([...selected, option.id]);
    }
  };

  return (
    <Popover open={open} onOpenChange={props.disabled ? undefined : setOpen}>
      <PickerShell
        id={props.id}
        open={open}
        disabled={props.disabled}
        invalid={props.invalid}
        required={props.required}
        describedBy={props.describedBy}
        className={props.className}
        buttonLabel={
          selected.length === 0 ? (
            <span className="text-fg-muted">{props.placeholder ?? 'Select…'}</span>
          ) : (
            <span className="text-fg-muted">Add…</span>
          )
        }
      >
        {selected.map((id) => {
          const label = labels.current.get(id) ?? props.displayLabel ?? id.slice(0, 8);
          return (
            <PickerChip
              key={id}
              label={label}
              onRemove={props.disabled ? undefined : () => toggle({ id, label })}
            />
          );
        })}
      </PickerShell>
      <PopoverContent className={popoverClass}>
        <PickerList
          load={props.load}
          open={open}
          selected={selectedSet}
          searchPlaceholder={props.searchPlaceholder}
          onPick={toggle}
        />
      </PopoverContent>
    </Popover>
  );
}
